// @flow
import React, { useState, useMemo } from 'react';
import type { Element } from 'react';

import filters from '@globalConstants/departmentTranslationCodes';

import type { FilterOption } from '@components/MobileFilters/types';
import { connect } from 'react-redux';
import compose from 'lodash/fp/compose';

import { MobileFilters, SelectFilter } from '@components/MobileFilters';

import { withTranslation, Router } from '@i18n';
import { FINDER_TREATMENT, TREATMENT_FILTER } from '@routes';
import { useBreakpoint } from '@hooks';
import { humanize, sortQueryParams } from '@lib/helpers';
import Select from './components/Select';
import StyledSelect from './components/StyledSelect';
import {
  StyledWrapper,
  StyledParametersContainer,
  StyledSeparationLine,
  StyledInnerWrapper,
  StyledMobileFiltersButton,
  StyledHeadlineText,
  StyledH1Container,
  StyledResetFilter,
  StyledResult
} from './styles';

import type { Props } from './types';
import { ITEMS_PER_PAGE } from '../redux/constants';

const SearchBar = ({
  t,
  isMapVisible,
  query,
  numberOfPages,
  TreatmentSearchData,
  department,
  objectList
}: Props): Element<any> => {
  const [isMobileFiltersOpen, setMobileFiltersOpen]: [boolean, Function] = useState(false);
  const totalItems = numberOfPages * ITEMS_PER_PAGE;
  const numberOfObjects: number = numberOfPages > 1 ? totalItems : objectList.length;
  const isDesktop: boolean = useBreakpoint('desktop');

  const setDepartment = ({ target }: { target?: { value: string } }): void => {
    const params = sortQueryParams(
      {
        ...query,
        department: target?.value || 'all-treatment',
        pageNumber: 1
      },
      ['query', 'department', 'pageNumber']
    );
    Router.pushRoute(TREATMENT_FILTER, params);
  };

  const setQuery = (target): void => {
    const params = sortQueryParams({ ...query, query: target?.key || '', pageNumber: 1 }, [
      'query',
      'department',
      'pageNumber'
    ]);
    Router.pushRoute(FINDER_TREATMENT, params);
  };

  const changeFilters = (values) => {
    const val = values;
    if (!values.department) {
      val.department = 'all-treatment';
    }
    const params = sortQueryParams({ ...val, pageNumber: 1 }, [
      'query',
      'department',
      'pageNumber'
    ]);
    Router.pushRoute(TREATMENT_FILTER, params);
  };

  const handleClose = () => {
    setMobileFiltersOpen(false);
  };

  const handleOpen = () => {
    setMobileFiltersOpen(true);
  };

  let treatmentNames;
  if (TreatmentSearchData !== undefined) {
    treatmentNames = TreatmentSearchData.map((obj) => {
      const rObj = {};
      rObj.key = obj.attributes.slug;
      rObj.value = obj.attributes.name;
      rObj.label = obj.attributes.name;
      return rObj;
    });
  }

  const mobileFilterDepartmentOptions: Array<FilterOption> = useMemo(
    () => filters.departments.map(value => ({ label: t(`departments.${value}`), value })),
    [filters.departments]
  );

  const mobileFilters = isMobileFiltersOpen ? (
    <MobileFilters
      values={{ department: query?.department, position: query?.query }}
      onClose={handleClose}
      onChange={changeFilters}
    >
      <SelectFilter
        filterKey="department"
        label={t('Department')}
        options={mobileFilterDepartmentOptions}
      />
    </MobileFilters>
  ) : (
    <StyledMobileFiltersButton onClick={handleOpen}>{t('FilterButton')}</StyledMobileFiltersButton>
  );

  const desktopFilters = (
    <>
      <Select label={t('Department')} onChange={setDepartment}>
        {filters.departments.map(department => (
          <option key={department} value={department} selected={department === query?.department}>
            {t(`departments.${department}`)}
          </option>
        ))}
      </Select>
    </>
  );

  const HeadLines: {
    Facilities: string,
  } = t('Facilities:Headlines', {
    returnObjects: true,
    department: department && department !== 'all-treatment' ? (humanize(department.split('_'))) : '',
    city: 'the World',
    facility: 'Treatments'
  });

  const clearFilters = () => {
    Router.pushRoute(FINDER_TREATMENT, { pageNumber: 1 });
  };

  return (
    <StyledWrapper isMapVisible={isMapVisible}>
      <StyledInnerWrapper isMapVisible={isMapVisible}>
        <StyledParametersContainer>
          <StyledSelect
            label={t('Name')}
            options={treatmentNames}
            name="treatment"
            isSearchable
            onChange={setQuery}
            query={query}
            placeholder={t('SelectYourTreatment')}
          />

          {isDesktop ? desktopFilters : mobileFilters}
        </StyledParametersContainer>


      </StyledInnerWrapper>
      <StyledHeadlineText>
        <StyledH1Container>{HeadLines.Facilities}</StyledH1Container>
        <StyledResult>
          {(query?.department || query?.query) && (
          <StyledResetFilter onClick={clearFilters}>
            reset filter
          </StyledResetFilter>
          )}
          <p>{t('XResults', { numberOfResults: numberOfObjects })}</p>
        </StyledResult>
      </StyledHeadlineText>
      <StyledSeparationLine />

    </StyledWrapper>
  );
};

const mapStateToProps = state => ({
  TreatmentSearchData: state.treatmentFinder.searchData
});

export default compose(
  connect(mapStateToProps),
  withTranslation(['Treatments'])
)(SearchBar);
